<template>
  <div class="requirement-manager">
    <header class="manager-header">
      <div>
        <div class="eyebrow">Lizenzlogik</div>
        <h2>Präsenzschulungen konfigurieren</h2>
        <p>Fasse mehrere Termine zu einem Schulungstyp zusammen und aktiviere die Präsenzpflicht pro Lizenzstufe bewusst.</p>
      </div>
      <v-btn color="#58d388" class="black--text create-action" depressed @click="openDialog()">
        <v-icon left>mdi-plus</v-icon>Schulungstyp
      </v-btn>
    </header>

    <v-alert type="info" outlined text class="manager-note">
      Erst die Aktivierung einer Stufe wirkt sich auf Lizenzen aus. Ein bestandener Typ zählt, sobald einer seiner Termine vorbei ist und die Person
      weiterhin als angemeldet oder teilgenommen geführt wird.
    </v-alert>

    <div v-if="loading" class="tier-grid">
      <v-skeleton-loader v-for="index in 3" :key="index" dark type="article, list-item-three-line, actions" class="tier-card" />
    </div>
    <div v-else class="tier-grid">
      <section v-for="tier in tiers" :key="tier.key" class="tier-card" :style="{ '--tier-color': tier.color }">
        <div class="tier-heading">
          <span><v-icon>mdi-medal-outline</v-icon></span>
          <div>
            <small>Lizenzstufe</small>
            <h3>{{ tier.label }}</h3>
          </div>
          <span class="state-badge" :class="{ active: requirementFor(tier.key).presenceRequired }">
            {{ requirementFor(tier.key).presenceRequired ? "Präsenzpflicht aktiv" : "Noch nicht aktiviert" }}
          </span>
        </div>

        <div v-if="typesFor(tier.key).length" class="type-list">
          <article v-for="type in typesFor(tier.key)" :key="type._id" class="type-row" :class="{ inactive: !type.active }">
            <span class="type-icon"><v-icon>{{ type.requiredForLicense ? "mdi-certificate-outline" : "mdi-calendar-outline" }}</v-icon></span>
            <div>
              <strong>{{ type.title }}</strong>
              <small>
                {{ type.requiredForLicense ? "Pflicht für Lizenz" : "Freiwillig" }} ·
                {{ (type.requiredModules && type.requiredModules.length) || 0 }} Online-Voraussetzungen
                <template v-if="!type.active"> · Deaktiviert</template>
              </small>
            </div>
            <v-btn icon small aria-label="Schulungstyp bearbeiten" @click="openDialog(type)"><v-icon small>mdi-pencil-outline</v-icon></v-btn>
            <v-btn icon small color="#ff8d98" aria-label="Schulungstyp löschen" @click="deleteType(type)"><v-icon small>mdi-delete-outline</v-icon></v-btn>
          </article>
        </div>
        <div v-else class="empty-types">
          <v-icon>mdi-calendar-blank-outline</v-icon>
          <span>Noch kein Schulungstyp für {{ tier.label }} erfasst.</span>
        </div>

        <footer class="tier-actions">
          <div>
            <strong>{{ requiredTypeCount(tier.key) }} Pflicht-Schulungstypen</strong>
            <small>Mindestens einer ist für die Aktivierung erforderlich.</small>
          </div>
          <v-btn
            v-if="!requirementFor(tier.key).presenceRequired"
            color="#009640"
            depressed
            :disabled="requiredTypeCount(tier.key) === 0"
            :loading="savingTier === tier.key"
            @click="setRequirement(tier.key, true)">
            <v-icon left>mdi-lock-check-outline</v-icon>Präsenzpflicht aktivieren
          </v-btn>
          <v-btn v-else outlined color="#ffb4bc" :loading="savingTier === tier.key" @click="setRequirement(tier.key, false)">
            <v-icon left>mdi-lock-open-outline</v-icon>Präsenzpflicht deaktivieren
          </v-btn>
        </footer>
      </section>
    </div>

    <v-dialog v-model="dialog" max-width="44rem" persistent dark scrollable :fullscreen="$vuetify.breakpoint.xsOnly">
      <v-card class="type-dialog">
        <header class="dialog-header">
          <span><v-icon>{{ editingId ? "mdi-calendar-edit" : "mdi-calendar-plus" }}</v-icon></span>
          <div>
            <div class="eyebrow">Präsenzschulung</div>
            <h2>{{ editingId ? "Schulungstyp bearbeiten" : "Schulungstyp erstellen" }}</h2>
            <p>Alle später zugeordneten Termine gelten als Alternativen. Ein beendeter Termin genügt.</p>
          </div>
          <v-btn icon aria-label="Dialog schliessen" :disabled="saving" @click="dialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </header>
        <v-card-text class="dialog-body">
          <v-form ref="form" v-model="valid">
            <v-text-field v-model="form.title" label="Bezeichnung" outlined :rules="[(value) => !!value || 'Bezeichnung erforderlich']" />
            <v-textarea v-model="form.description" label="Beschreibung (optional)" outlined rows="3" />
            <v-select
              v-model="form.tier"
              :items="tiers"
              item-text="label"
              item-value="key"
              label="Lizenzstufe"
              outlined
              :rules="[(value) => !!value || 'Lizenzstufe erforderlich']" />
            <v-select
              v-model="form.requiredModules"
              :items="moduleOptions"
              item-text="title"
              item-value="_id"
              label="Online-Module als Buchungsvoraussetzung"
              hint="Diese Module müssen vor der Anmeldung an einen Termin dieses Typs abgeschlossen sein."
              persistent-hint
              multiple
              chips
              outlined />
            <div class="dialog-settings">
              <div>
                <strong>Pflicht für die Lizenz</strong>
                <span>Nach Aktivierung der Stufe muss mindestens einer der zugehörigen Termine abgeschlossen sein.</span>
                <v-switch v-model="form.requiredForLicense" color="#58d388" inset hide-details aria-label="Pflicht für Lizenz" />
              </div>
              <div>
                <strong>Schulungstyp aktiv</strong>
                <span>Deaktivierte Typen können keinen neuen Terminen zugeordnet werden.</span>
                <v-switch v-model="form.active" color="#58d388" inset hide-details aria-label="Schulungstyp aktiv" />
              </div>
            </div>
            <v-alert
              v-if="form.requiredForLicense && form.active && requirementFor(form.tier).presenceRequired"
              type="warning"
              outlined
              text
              class="active-requirement-note">
              Die Präsenzpflicht dieser Stufe ist bereits aktiv. Dieser Schulungstyp wird nach dem Speichern sofort bei bestehenden Lizenzen
              berücksichtigt.
            </v-alert>
          </v-form>
          <v-alert v-if="dialogError" type="error" outlined text class="mt-4 mb-0">{{ dialogError }}</v-alert>
        </v-card-text>
        <v-card-actions class="dialog-actions">
          <v-btn text :disabled="saving" @click="dialog = false">Abbrechen</v-btn>
          <v-spacer />
          <v-btn color="#58d388" class="black--text" depressed :loading="saving" :disabled="!valid || saving" @click="saveType">
            <v-icon left>mdi-content-save-outline</v-icon>Speichern
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="4000" top right>{{ snackbar.text }}</v-snackbar>
  </div>
</template>

<script>
import api from "@/api"

export default {
  name: "TrainingRequirementManager",
  data() {
    return {
      tiers: [
        { key: "bronze", label: "Bronze", color: "#d89557" },
        { key: "silber", label: "Silber", color: "#d4d4d8" },
        { key: "gold", label: "Gold", color: "#f4c95d" },
      ],
      types: [],
      requirements: [],
      modules: [],
      loading: false,
      dialog: false,
      valid: false,
      editingId: null,
      saving: false,
      savingTier: "",
      dialogError: "",
      form: this.emptyForm(),
      snackbar: { show: false, color: "success", text: "" },
    }
  },
  computed: {
    moduleOptions() {
      return this.modules.filter((module) => module.tier === this.form.tier && module.active !== false)
    },
  },
  mounted() {
    this.loadData()
  },
  methods: {
    emptyForm() {
      return { title: "", description: "", tier: "bronze", requiredModules: [], requiredForLicense: true, active: true }
    },
    notify(color, text) {
      this.snackbar = { show: true, color, text }
    },
    requirementFor(tier) {
      return this.requirements.find((item) => item.tier === tier) || { tier, presenceRequired: false, requiredTypeCount: 0 }
    },
    typesFor(tier) {
      return this.types.filter((type) => type.tier === tier)
    },
    requiredTypeCount(tier) {
      return this.typesFor(tier).filter((type) => type.active && type.requiredForLicense).length
    },
    async loadData() {
      this.loading = true
      try {
        const [types, requirements, modules] = await Promise.all([
          api.akademie.fetchTrainingTypes(),
          api.akademie.fetchLicenseRequirements(),
          api.akademie.fetchModules(),
        ])
        this.types = types || []
        this.requirements = requirements || []
        this.modules = modules || []
      } catch (error) {
        this.notify("error", "Die Präsenzanforderungen konnten nicht geladen werden.")
      } finally {
        this.loading = false
      }
    },
    openDialog(type = null) {
      this.editingId = type && type._id
      this.form = type
        ? {
            title: type.title || "",
            description: type.description || "",
            tier: type.tier || "bronze",
            requiredModules: (type.requiredModules || []).map((module) => module._id || module),
            requiredForLicense: !!type.requiredForLicense,
            active: !!type.active,
          }
        : this.emptyForm()
      this.dialogError = ""
      this.dialog = true
      this.$nextTick(() => {
        if (this.$refs.form) this.$refs.form.resetValidation()
      })
    },
    async saveType() {
      if (!this.$refs.form || !this.$refs.form.validate() || this.saving) return
      this.saving = true
      this.dialogError = ""
      try {
        if (this.editingId) await api.akademie.updateTrainingType(this.editingId, this.form)
        else await api.akademie.createTrainingType(this.form)
        this.dialog = false
        await this.loadData()
        this.notify("success", "Schulungstyp gespeichert.")
      } catch (error) {
        this.dialogError =
          (error.response && error.response.data && (error.response.data.error || error.response.data.message)) ||
          "Der Schulungstyp konnte nicht gespeichert werden."
      } finally {
        this.saving = false
      }
    },
    async deleteType(type) {
      if (!confirm(`Schulungstyp «${type.title}» wirklich löschen? Bereits verwendete Typen können stattdessen deaktiviert werden.`)) return
      try {
        await api.akademie.deleteTrainingType(type._id)
        await this.loadData()
        this.notify("success", "Schulungstyp gelöscht.")
      } catch (error) {
        const message = (error.response && error.response.data && error.response.data.error) || "Der Schulungstyp konnte nicht gelöscht werden."
        this.notify("error", message)
      }
    },
    async setRequirement(tier, presenceRequired) {
      const action = presenceRequired ? "aktivieren" : "deaktivieren"
      const detail = presenceRequired
        ? "Bestehende Lizenzen dieser Stufe werden sofort neu geprüft. Ohne bereits beendeten passenden Termin werden sie gesperrt – auch wenn noch kein neuer Termin ausgeschrieben ist."
        : "Die Präsenzschulungen blockieren danach keine Lizenzen mehr."
      if (!confirm(`Präsenzpflicht für ${tier} ${action}?\n\n${detail}`)) return
      this.savingTier = tier
      try {
        await api.akademie.updateLicenseRequirement(tier, presenceRequired)
        await this.loadData()
        this.notify("success", `Präsenzpflicht für ${tier} wurde ${presenceRequired ? "aktiviert" : "deaktiviert"}.`)
      } catch (error) {
        const message = (error.response && error.response.data && error.response.data.error) || "Die Präsenzpflicht konnte nicht gespeichert werden."
        this.notify("error", message)
      } finally {
        this.savingTier = ""
      }
    },
  },
}
</script>

<style scoped>
.requirement-manager {
  color: #f4f4f5;
  font-size: 1rem;
}

.manager-header,
.dialog-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
}

.eyebrow,
.tier-heading small {
  color: #7ee5a6;
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.manager-header h2,
.dialog-header h2 {
  margin: 0.1875rem 0 0.25rem;
  color: #fff;
  font-size: 1.375rem;
  line-height: 1.3;
}

.manager-header p,
.dialog-header p {
  margin: 0;
  color: #a3a3a8;
  font-size: 0.875rem;
  line-height: 1.5;
}

.create-action,
.tier-actions .v-btn,
.dialog-actions .v-btn {
  min-height: 2.75rem;
  border-radius: 0.8125rem !important;
  font-size: 0.875rem;
  font-weight: 750;
  text-transform: none;
}

.manager-note {
  margin: 1rem 0 !important;
  font-size: 0.875rem;
}

.tier-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.875rem;
}

.tier-card {
  min-width: 0;
  padding: 1rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-top: 0.1875rem solid var(--tier-color);
  border-radius: 1rem;
  background: rgba(255, 255, 255, 0.025);
}

.tier-heading {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 0.625rem;
}

.tier-heading > span:first-child,
.type-icon,
.dialog-header > span {
  display: grid;
  place-items: center;
  color: var(--tier-color, #7ee5a6);
  background: rgba(255, 255, 255, 0.06);
}

.tier-heading > span:first-child {
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 0.75rem;
}

.tier-heading .v-icon,
.type-icon .v-icon,
.dialog-header > span .v-icon {
  color: currentColor;
}

.tier-heading h3 {
  margin: 0;
  color: #fff;
  font-size: 1.125rem;
}

.state-badge {
  padding: 0.375rem 0.625rem;
  border-radius: 999px;
  color: #b8b8bc;
  background: rgba(255, 255, 255, 0.06);
  font-size: 0.75rem;
  font-weight: 750;
}

.state-badge.active {
  color: #7ee5a6;
  background: rgba(0, 150, 64, 0.13);
}

.type-list {
  display: grid;
  gap: 0.5rem;
  margin-top: 1rem;
}

.type-row {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto auto;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 0.75rem;
  background: rgba(0, 0, 0, 0.13);
}

.type-row.inactive {
  opacity: 0.65;
}

.type-icon {
  width: 2.25rem;
  height: 2.25rem;
  border-radius: 0.6875rem;
}

.type-row > div {
  display: grid;
  min-width: 0;
  gap: 0.125rem;
}

.type-row strong {
  overflow: hidden;
  color: #fff;
  font-size: 0.875rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.type-row small,
.tier-actions small {
  color: #8f9095;
  font-size: 0.75rem;
  line-height: 1.4;
}

.empty-types {
  display: flex;
  min-height: 7rem;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  color: #8f9095;
  font-size: 0.875rem;
  text-align: center;
}

.empty-types .v-icon {
  color: #8f9095;
}

.tier-actions {
  display: grid;
  gap: 0.75rem;
  margin-top: 1rem;
  padding-top: 0.875rem;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
}

.tier-actions > div {
  display: grid;
  gap: 0.125rem;
}

.tier-actions strong {
  color: #d4d4d8;
  font-size: 0.875rem;
}

.type-dialog {
  color: #f4f4f5;
  background: #191919 !important;
  font-size: 1rem;
}

.dialog-header {
  padding: 1.25rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.dialog-header > span {
  width: 2.75rem;
  height: 2.75rem;
  flex: 0 0 2.75rem;
  border-radius: 0.8125rem;
}

.dialog-header > div {
  min-width: 0;
  flex: 1 1 auto;
}

.dialog-body {
  padding: 1.25rem !important;
}

.dialog-settings {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;
}

.dialog-settings > div {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: 0.25rem 0.75rem;
  padding: 0.875rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 0.875rem;
  background: rgba(0, 0, 0, 0.14);
}

.dialog-settings strong {
  color: #fff;
  font-size: 0.875rem;
}

.dialog-settings span {
  grid-column: 1;
  color: #a3a3a8;
  font-size: 0.75rem;
  line-height: 1.45;
}

.dialog-settings .v-input {
  grid-column: 2;
  grid-row: 1 / span 2;
}

.active-requirement-note {
  margin: 1rem 0 0 !important;
  font-size: 0.875rem;
}

.dialog-actions {
  padding: 1rem 1.25rem !important;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

@media (max-width: 78rem) {
  .tier-grid {
    grid-template-columns: minmax(0, 1fr);
  }
}

@media (max-width: 43rem) {
  .manager-header {
    align-items: stretch;
    flex-direction: column;
  }

  .tier-heading {
    grid-template-columns: auto minmax(0, 1fr);
  }

  .state-badge {
    grid-column: 2;
    justify-self: start;
  }

  .dialog-settings {
    grid-template-columns: minmax(0, 1fr);
  }
}
</style>
